import { useEffect, useRef, useState } from "react";
import { ChevronRight, CircleAlert, Link2, ShieldCheck, Sparkles } from "lucide-react";
import {
  GatewayAuthAPI, HermesError, RelayInviteCode, Welcome, parsePairingLink, relayHub, supportsNativeLogin, verifyGateway,
  type AuthProviderInfo, type GatewayCredential, type GatewayEndpoint, type RelayPairing, type RelayRoute, type RelayWelcome,
  type ServerStatus,
} from "@timibot/core";
import { useApp } from "../../context";
import type { ConnectMode } from "../../state/app";
import { copyText } from "../../ui/clipboard";
import { Menu, PillButton, PrimaryButton, Spinner, useMenu } from "../../ui/components";
import { TimiMascot } from "../../ui/Mascot";
import "./onboarding.css";

/** TimiBot 中转服务的地址（主机上 install.sh / connector 使用的同一个） */
export const RelayDefaults = {
  url: "https://relay.sicreed.com",
  get installCommand() { return `curl -fsSL ${this.url}/install.sh | sh`; },
  /** 第 1 步复制给主机上的 Hermes Agent（或自己在终端照做）：没装就装，装过就生成新的配对码 */
  get agentPrompt() {
    return `帮我把这台机器上的 Hermes 连接到 TimiBot：在终端运行\n${this.installCommand}\n没装过会自动安装；装过会保留原来的配对，只生成新的配对码。运行完把输出里的 8 位配对码（形如 ABCD-EFGH）告诉我。`;
  },
};

/**
 * 连接主机（对应 iOS 的 OnboardingView）：两步——让主机上的 Hermes 生成 8 位配对码，在这里输入
 * （朋友分享 Bot 的邀请码也在同一个框里输，也可以直接粘贴整条 timibot://pair 链接）。
 * 经 TimiBot 中转端到端加密连接。主机上的 Hermes 开了登录门禁时，再用 Hermes 账号经隧道登录一次。
 */
export function Onboarding({ mode }: { mode: ConnectMode }) {
  const app = useApp();
  const [status, setStatus] = useState<ServerStatus>();
  const [passwordProviders, setPasswordProviders] = useState<AuthProviderInfo[]>([]);
  const [otherProviders, setOtherProviders] = useState<AuthProviderInfo[]>([]);
  const [provider, setProvider] = useState("");
  const [username, setUsername] = useState(app.server?.username ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [route, setRoute] = useState<RelayRoute>();
  const [welcome, setWelcome] = useState<RelayWelcome>();
  const [invite, setInvite] = useState<RelayPairing>();
  const [codeText, setCodeText] = useState("");
  const [copied, setCopied] = useState(false);
  const switchMenu = useMenu();
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (app.pendingPairing) {
      const p = app.pendingPairing;
      app.pendingPairing = undefined;
      void pair(p);
      return;
    }
    if (mode !== "add" && app.server?.endpoint.kind === "relay") {
      setRoute(app.server.endpoint.relay);
      if (mode === "reauth") void reconnectRelay(app.server.endpoint.relay);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const endpoint = (r = route): GatewayEndpoint => {
    if (!r) throw HermesError.invalidConfiguration("请先输入配对码");
    return { kind: "relay", relay: r };
  };

  /** 配对码 / 邀请码 → 向中转换回配对链接 → 正常配对 */
  const redeem = async (raw: string) => {
    const link = parsePairingLink(raw);
    if (link) { setCodeText(""); await pair(link); return; }
    const code = RelayInviteCode.normalize(raw);
    if (!code) { setError("配对码是 8 位字母和数字，例如 K7QM-3XPA"); return; }
    setError(undefined);
    setBusy(true);
    try {
      const relay = app.server?.endpoint.kind === "relay" ? app.server.endpoint.relay.relayURL : RelayDefaults.url;
      const p = await RelayInviteCode.resolve(code, relay);
      setBusy(false);
      await pair(p);
    } catch (e) {
      setError(message(e));
      setBusy(false);
    }
  };

  const pair = async (pairing: RelayPairing) => {
    setError(undefined);
    setStatus(undefined);
    setWelcome(undefined);
    const r = app.routeForPairing(pairing);
    setRoute(r);
    setInvite(pairing.sharedBot ? pairing : undefined);
    if (pairing.username) setUsername(pairing.username);
    if (pairing.password) setPassword(pairing.password);
    setBusy(true);
    try {
      const w = await relayHub.tunnel(r).pair(pairing.code);
      setWelcome(w);
      await continueAfterHandshake(w, r, pairing.sharedBot !== undefined, pairing.username, pairing.password);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  /** 重新连接：已配对的主机不需要配对码，直接握手 */
  const reconnectRelay = async (r: RelayRoute) => {
    setError(undefined);
    setBusy(true);
    try {
      const w = await relayHub.tunnel(r).pair(undefined);
      setWelcome(w);
      await continueAfterHandshake(w, r, false, undefined, undefined);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  /** 握手成功：主机的 Hermes 没开门禁就直接完成；开了门禁就走 Hermes 账号登录（请求同样经隧道） */
  const continueAfterHandshake = async (w: RelayWelcome, r: RelayRoute, wasInvite: boolean, u?: string, pw?: string) => {
    if (w.backend_error) throw HermesError.transport(w.backend_error);
    const ep = endpoint(r);
    if (Welcome.requiresLogin(w)) {
      const s = await probeLogin(ep);
      if (s?.auth_required && pw && u) await login(ep, u, pw, w);
      return;
    }
    await verifyGateway(ep, { type: "tunnel" });
    const s = await new GatewayAuthAPI(ep).status().catch(() => undefined);
    finish(ep, { type: "tunnel" }, undefined, undefined, s, w, wasInvite);
  };

  /** 主机开了门禁：经隧道查可用的登录方式 */
  const probeLogin = async (ep: GatewayEndpoint): Promise<ServerStatus | undefined> => {
    const api = new GatewayAuthAPI(ep);
    const s = await api.status();
    if (s.auth_required) {
      const providers = await api.providers().catch(() => []);
      const pw = providers.filter((p) => p.supports_password);
      setPasswordProviders(pw);
      setOtherProviders(providers.filter((p) => !p.supports_password));
      setProvider(pw[0]?.name ?? "");
      if (pw.length && !supportsNativeLogin(s)) setError("主机上的 Hermes 版本较旧，不支持原生登录（需要 Hermes ≥ 0.21）");
    }
    setStatus(s);
    setTimeout(() => passwordRef.current?.focus(), 50);
    return s;
  };

  const login = async (ep = endpoint(), u = username, pw = password, w = welcome, prov = provider) => {
    setError(undefined);
    setBusy(true);
    try {
      const tokens = await new GatewayAuthAPI(ep).passwordLogin(prov || passwordProviders[0]?.name || "basic", u, pw);
      const credential: GatewayCredential = { type: "bearer", tokens };
      await verifyGateway(ep, credential);
      setPassword("");
      finish(ep, credential, u, prov, status, w, invite !== undefined);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  const finish = (ep: GatewayEndpoint, credential: GatewayCredential, u: string | undefined, prov: string | undefined,
    s: ServerStatus | undefined, w: RelayWelcome | undefined, wasInvite: boolean) => {
    if (wasInvite && w && !Welcome.isGuest(w)) app.showToast("你已经是这台主机的主人，可以直接使用所有 Bot");
    app.completeSignIn(ep, credential, u, prov, s, w);
  };

  const reset = () => {
    setCodeText("");
    setRoute(undefined);
    setInvite(undefined);
    setWelcome(undefined);
    setStatus(undefined);
    setError(undefined);
  };

  const showLogin = route && status?.auth_required;

  return (
    <div className={`onboarding scroll${mode === "firstRun" ? " first-run" : ""}`}>
      <div className="onboarding-column">
        <header className="onboarding-header">
          {mode === "firstRun" ? (
            <>
              <TimiMascot size={64} />
              <h1 className="t-large-title">连接你的 Hermes</h1>
            </>
          ) : mode === "add" ? <h1 className="t-large-title">添加配对</h1> : (
            <>
              <h1 className="t-large-title">重新连接</h1>
              <p className="t-subheadline t-2">和主机的配对或登录失效了，请重新验证。</p>
            </>
          )}
        </header>

        {route ? (
          <div className="card onboarding-host">
            {welcome ? <ShieldCheck size={26} style={{ color: "var(--success)" }} /> : <Link2 size={26} className="t-2" />}
            <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
              <span className="t-subheadline semibold nowrap">{route.name}</span>
              <span className="t-caption t-2">
                {welcome && Welcome.isGuest(welcome) ? "朋友分享给你的 Bot · 端到端加密"
                  : welcome ? `${welcome.hermes ? `Hermes v${welcome.hermes} · ` : ""}${Welcome.requiresLogin(welcome) ? "已配对，这台 Hermes 开了登录，请登录一次" : "已配对 · 端到端加密"}`
                  : busy ? "正在经 TimiBot 中转配对…" : "未连接"}
              </span>
            </div>
            {busy && <Spinner />}
          </div>
        ) : (
          <>
            <div className="card onboarding-step">
              <div className="row">
                <span className="t-subheadline semibold">1. 把这段话发给你的 Hermes</span>
                <span className="spacer" />
                <PillButton title={copied ? "已复制" : "复制"} prominent onClick={() => { void copyText(RelayDefaults.agentPrompt); setCopied(true); }} />
              </div>
              <p className="t-footnote t-2 onboarding-prompt">{RelayDefaults.agentPrompt}</p>
            </div>
            <div className="card onboarding-step">
              <span className="t-subheadline semibold">2. 输入配对码</span>
              <input
                className="code-input mono"
                placeholder="ABCD-EFGH"
                value={codeText}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="one-time-code"
                enterKeyHint="go"
                onChange={(e) => {
                  const text = e.target.value;
                  // 粘贴整条配对链接也行
                  const link = parsePairingLink(text);
                  if (link) { setCodeText(""); void pair(link); } else setCodeText(text);
                }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) void redeem(codeText); }}
              />
              <PrimaryButton title="连接" busy={busy} disabled={!RelayInviteCode.normalize(codeText) && !parsePairingLink(codeText)} onClick={() => void redeem(codeText)} />
              <span className="t-caption t-3">朋友分享 Bot 的邀请码也在这里输入</span>
            </div>
          </>
        )}

        {showLogin && (passwordProviders.length === 0 ? (
          <div className="card onboarding-step">
            <span className="t-subheadline semibold">这台 Hermes 没有启用用户名密码登录</span>
            <span className="t-footnote t-2">
              {otherProviders.length === 0
                ? "它开了登录门禁（通常是配置了公网 dashboard.public_url）。去掉门禁后 TimiBot 可以免登录连接。"
                : `当前只启用了 ${otherProviders.map((p) => p.display_name).join("、")} 登录。TimiBot 目前支持用户名密码方式，请在主机上额外启用 basic 登录（两者可以并存）。`}
            </span>
          </div>
        ) : (
          <form className="onboarding-login" onSubmit={(e) => { e.preventDefault(); void login(); }}>
            {passwordProviders.length > 1 && (
              <div className="pill-switcher">
                {passwordProviders.map((p) => (
                  <button type="button" key={p.name} className={provider === p.name ? "on" : ""} onClick={() => setProvider(p.name)}>{p.display_name}</button>
                ))}
              </div>
            )}
            <input className="field" placeholder="用户名" autoComplete="username" autoCapitalize="none" autoCorrect="off" value={username} onChange={(e) => setUsername(e.target.value)} />
            <input ref={passwordRef} className="field" placeholder="密码" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <PrimaryButton title="登录" busy={busy} disabled={!username || !password} onClick={() => void login()} />
          </form>
        ))}

        {error && <div className="onboarding-error t-footnote"><CircleAlert size={14} /><span>{error}</span></div>}

        {mode === "reauth" && app.savedServers.length > 0 ? (
          <>
            <button className="t-footnote t-brand onboarding-link" onClick={(e) => switchMenu.show(e.currentTarget)}>切换连接</button>
            <Menu open={switchMenu.open} anchor={switchMenu.anchor} onClose={switchMenu.close} align="start" items={[
              ...app.savedServers.map((s) => ({ label: s.endpoint.kind === "relay" ? s.endpoint.relay.name : "体验模式", onSelect: () => app.switchConnection(s) })),
              { label: "删除当前连接", destructive: true, onSelect: () => app.signOut() },
            ]} />
          </>
        ) : route && !busy ? (
          <button className="t-footnote t-brand onboarding-link" onClick={reset}>换一台主机</button>
        ) : null}

        {mode === "firstRun" && !route && (
          <button className="card onboarding-demo" onClick={() => app.startDemo()}>
            <Sparkles size={22} className="t-brand" />
            <span className="col" style={{ flex: 1, gap: 2, textAlign: "left" }}>
              <span className="t-subheadline semibold">还没有主机？先体验一下</span>
              <span className="t-caption t-2">用示例数据看看 TimiBot 能做什么，不需要任何配置</span>
            </span>
            <ChevronRight size={14} className="t-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
